import React, { useState } from 'react';

import SaveIcon from '@material-ui/icons/Save';
import { default as PasswordIcon } from '@material-ui/icons/Lock';
import { default as EditIcon } from '@material-ui/icons/Edit';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import {
    deleteUser,
    resetPassword,
} from '../../../../../../state/app/collections';

import { useAppDispatch } from '../../../../../../hooks';
import useToggle from '../../../../../../hooks/useToggle';

import TranslationHelper from '../../../../../../helpers/TranslationHelper';

import DeleteButton from '../../../../../../components/DeleteButton';
import PaneHeader from '../../../../../../components/PaneHeader';
import IconButtonWithTooltip from '../../../../../../components/IconButtonWithTooltip';
import AssignRightsDialog from '../AssignRightsDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import { showUserEditionDialog } from '../../../../../../state/ui/dialog';
import { IUser } from '@/app/state/app/collections/dataTypes';

interface IOwnProps {
    user?: IUser;
    title: string;
    userDataRightEnabled?: boolean;
    editionEnabled?: boolean;
    deletionEnabled?: boolean;
    passwordResetEnabled?: boolean;
    onClose?: () => void;
    onSave?: () => void;
    onUpdate?: () => void;
}

const UserPaneHeader = ({
    user,
    title,
    userDataRightEnabled,
    editionEnabled,
    deletionEnabled,
    passwordResetEnabled,
    onClose,
    onSave,
    onUpdate,
}: IOwnProps) => {
    const {
        isOpen: isDataRightsDialogOpen,
        handleToggle: toggleDataRightsDialog,
    } = useToggle();

    const [isUserResetDialogOpen, setUserResetDialogOpen] = useState(false);

    const dispatch = useAppDispatch();

    const deleteUserHandler = () => {
        user && dispatch(deleteUser(user.id));
    };
    const handleCloseClick = () => {
        onClose?.();
    };

    const handleResetPasswordOpenDialog = () => {
        setUserResetDialogOpen(true);
    };

    const handleResetPasswordClick = () => {
        user &&
            dispatch(
                resetPassword(user.id, () => setUserResetDialogOpen(false))
            );
    };

    const handleResetPasswordDialogClose = () => {
        setUserResetDialogOpen(false);
    };

    const allowedToResetPassword = () => {
        return !user?.isGlobal;
    };

    const handleEditDialogSave = () => {
        onUpdate?.();
    };

    const handleEditDialogOpen = () => {
        user &&
            dispatch(
                showUserEditionDialog({
                    selectedUser: user,
                    onSave: handleEditDialogSave,
                })
            );
    };

    return (
        <>
            <PaneHeader
                title={title}
                onCloseClick={handleCloseClick}
                renderRightCustomControls={() => (
                    <>
                        {user ? (
                            <>
                                {userDataRightEnabled && (
                                    <IconButtonWithTooltip
                                        title={TranslationHelper.translate(
                                            'Data access rights'
                                        )}
                                        onClick={toggleDataRightsDialog}
                                    >
                                        <CategoryOutlinedIcon />
                                    </IconButtonWithTooltip>
                                )}
                                {passwordResetEnabled && (
                                    <IconButtonWithTooltip
                                        title={
                                            TranslationHelper.translate(
                                                'Reset password'
                                            ) +
                                            (!allowedToResetPassword()
                                                ? ' (' +
                                                  TranslationHelper.translate(
                                                      'disabled for global users'
                                                  ) +
                                                  ')'
                                                : '')
                                        }
                                        onClick={handleResetPasswordOpenDialog}
                                        disabled={!allowedToResetPassword()}
                                    >
                                        <PasswordIcon />
                                    </IconButtonWithTooltip>
                                )}
                                {editionEnabled && (
                                    <>
                                        <IconButtonWithTooltip
                                            title={TranslationHelper.translate(
                                                'Edit user'
                                            )}
                                            onClick={handleEditDialogOpen}
                                        >
                                            <EditIcon />
                                        </IconButtonWithTooltip>
                                    </>
                                )}
                                {deletionEnabled && (
                                    <DeleteButton
                                        title="User removal confirmation"
                                        tooltip="Delete user"
                                        message="Do you really want to DELETE this user?"
                                        deleteMethod={deleteUserHandler}
                                    />
                                )}
                                {isDataRightsDialogOpen && (
                                    <AssignRightsDialog
                                        isOpen={isDataRightsDialogOpen}
                                        closeDialog={toggleDataRightsDialog}
                                        userId={user.id}
                                    />
                                )}
                                {isUserResetDialogOpen && (
                                    <ResetPasswordDialog
                                        onSubmit={handleResetPasswordClick}
                                        close={handleResetPasswordDialogClose}
                                        canResetPassword={
                                            user.email !== undefined &&
                                            user.email !== ''
                                        }
                                    />
                                )}
                            </>
                        ) : null}
                        {onSave && (
                            <IconButtonWithTooltip
                                title={TranslationHelper.translate('Save')}
                                onClick={onSave}
                            >
                                <SaveIcon />
                            </IconButtonWithTooltip>
                        )}
                    </>
                )}
            />
        </>
    );
};

export default UserPaneHeader;
